import React, { useEffect, useState, useCallback } from 'react';
import { timeAgo, fmtDate, fmtDateTime, parseUTC } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import { announcementApi, departmentApi } from '../api';
import { Btn, PageHeader, EmptyState, Avatar } from '../components/ui';

function AnnouncementCard({ a, onDelete, onPin, currentUserId, userRole }) {
  const isAdminPlus = userRole === 'ADMIN' || userRole === 'SUPER_ADMIN';
  const canDelete   = a.canDelete;
  const canPin      = isAdminPlus;
  const d = parseUTC(a.createdAt);
  const timeStr = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${a.pinned ? '#FDE68A' : '#E5E7EB'}`,
      borderRadius: '12px',
      padding: '14px 16px',
      boxShadow: a.pinned ? '0 0 0 1px #F59E0B33' : '0 1px 3px rgba(0,0,0,.04)',
    }}>
      {a.pinned && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
          <span style={{ fontSize: '12px', fontWeight: 700, color: '#B45309', background: '#FEF3C7', padding: '2px 8px', borderRadius: '999px' }}>
            📌 Pinned
          </span>
          {a.departmentName && (
            <span style={{ fontSize: '12px', color: '#6B7280', background: '#F1F5F9', padding: '2px 8px', borderRadius: '999px' }}>
              {a.departmentName}
            </span>
          )}
        </div>
      )}

      {!a.pinned && a.departmentName && (
        <span style={{ fontSize: '11px', color: '#6B7280', background: '#F1F5F9', padding: '2px 8px', borderRadius: '999px', display: 'inline-block', marginBottom: '8px' }}>
          {a.departmentName}
        </span>
      )}

      <p style={{ fontSize: '15px', color: '#111827', lineHeight: 1.65, marginBottom: '12px', whiteSpace: 'pre-wrap' }}>
        {a.content}
      </p>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Avatar name={a.authorName} size={24} />
          <div>
            <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>{a.authorName}</span>
            <span style={{ fontSize: '11px', color: '#9CA3AF', marginLeft: '6px' }}>{a.authorRole?.replace('_', ' ')}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{timeStr}</span>
          {canPin && (
            <button onClick={() => onPin(a.id)} style={S.iconBtn} title={a.pinned ? 'Unpin' : 'Pin'}>
              {a.pinned ? '📌' : '📍'}
            </button>
          )}
          {canDelete && (
            <button onClick={() => onDelete(a.id)} style={{ ...S.iconBtn, color: '#DC2626' }} title="Remove">✕</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Announcements() {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [departments,   setDepartments]   = useState([]);
  const [deptFilter,    setDeptFilter]    = useState('');
  const [loading,       setLoading]       = useState(true);
  const [posting,       setPosting]       = useState(false);
  const [error,         setError]         = useState('');
  const [content,       setContent]       = useState('');
  const [selectedDept,  setSelectedDept]  = useState('');
  const [pinned,        setPinned]        = useState(false);

  const isAdminPlus = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [aRes, dRes] = await Promise.all([
        deptFilter === 'company'
          ? announcementApi.getCompanyWide()
          : announcementApi.getAll(deptFilter || null),
        departmentApi.getAll(),
      ]);
      setAnnouncements(aRes.data?.data || []);
      setDepartments(dRes.data?.data || []);
    } catch (e) {
      setError('Could not load announcements. ' + (e.response?.data?.message || 'Please try again.'));
    } finally { setLoading(false); }
  }, [deptFilter]);

  useEffect(() => { load(); }, [load]);

  const handlePost = async () => {
    if (!content.trim()) { setError('Write something first'); return; }
    setPosting(true); setError('');
    try {
      await announcementApi.create({
        content: content.trim(),
        departmentId: selectedDept || null,
        pinned: pinned && isAdminPlus,
      });
      setContent(''); setSelectedDept(''); setPinned(false);
      load();
    } catch (e) { setError(e.response?.data?.message || 'Failed to post'); }
    finally { setPosting(false); }
  };

  const handleDelete = async (id) => {
    try { await announcementApi.archive(id); load(); }
    catch (e) { setError(e.response?.data?.message || 'Failed'); }
  };

  const handlePin = async (id) => {
    try { await announcementApi.togglePin(id); load(); }
    catch (e) { setError(e.response?.data?.message || 'Failed'); }
  };

  return (
    <div style={{ padding: '20px 16px', maxWidth: '680px', margin: '0 auto' }}>
      <PageHeader title="Announcements"
        subtitle="Posts are auto-archived at end of day. All posts are logged." />

      {error && <div style={S.err}>{error}</div>}

      {/* Post box */}
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '14px', padding: '16px', marginBottom: '20px', boxShadow: '0 1px 4px rgba(0,0,0,.04)' }}>
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder={`Post an announcement to ${selectedDept ? departments.find(d => d.id === selectedDept)?.name || 'department' : 'the whole company'}…`}
          rows={3}
          style={{
            width: '100%', border: 'none', outline: 'none', resize: 'vertical',
            fontSize: '15px', color: '#111827', fontFamily: 'inherit',
            background: 'transparent', lineHeight: 1.65, marginBottom: '12px',
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={selectedDept} onChange={e => setSelectedDept(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '13px', background: '#F9FAFB', cursor: 'pointer' }}>
              <option value="">Company-wide</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            {isAdminPlus && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
                <input type="checkbox" checked={pinned} onChange={e => setPinned(e.target.checked)}
                  style={{ width: '14px', height: '14px' }} />
                Pin
              </label>
            )}
          </div>
          <Btn size="sm" onClick={handlePost} loading={posting} disabled={!content.trim()}>Post →</Btn>
        </div>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', overflowX: 'auto', paddingBottom: '4px', scrollbarWidth: 'none' }}>
        <button onClick={() => setDeptFilter('')} style={chipStyle(deptFilter === '')}>All</button>
        <button onClick={() => setDeptFilter('company')} style={chipStyle(deptFilter === 'company')}>Company-wide</button>
        {departments.map(d => (
          <button key={d.id} onClick={() => setDeptFilter(d.id)} style={chipStyle(deptFilter === d.id)}>
            {d.name}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height: '100px', borderRadius: '12px' }} />)}
        </div>
      ) : announcements.length === 0 ? (
        <EmptyState icon="📢" title="Nothing posted today"
          subtitle="Be the first to post an announcement." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {announcements.map(a => (
            <AnnouncementCard key={a.id} a={a}
              onDelete={handleDelete} onPin={handlePin}
              currentUserId={user?.id} userRole={user?.role} />
          ))}
        </div>
      )}
    </div>
  );
}

const chipStyle = (active) => ({
  padding: '6px 14px', borderRadius: '999px', cursor: 'pointer',
  fontSize: '13px', fontWeight: active ? 700 : 400, whiteSpace: 'nowrap',
  background: active ? '#111827' : '#fff',
  color: active ? '#fff' : '#6B7280',
  border: `1.5px solid ${active ? '#111827' : '#E5E7EB'}`,
  transition: 'all 0.12s',
});

const S = {
  err:    { padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', color: '#DC2626', fontSize: '14px', marginBottom: '16px' },
  iconBtn:{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '2px 4px', borderRadius: '4px', lineHeight: 1 },
};
